<script setup lang="ts">
import { computed, ref, resolveComponent } from 'vue';
import { THEME } from '@pkg/config/misc/theme';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselStepCounter,
  type CarouselApi,
} from '@pkg/ui/components/ui/carousel';
import { type Image } from '@sanity/types';
import { watchOnce } from '@vueuse/core';
import { cn } from '@pkg/ui/lib/utils';
import type { ModularBlock } from '@pkg/sanity-utils/types/modularContentBlocks';
import type { Button } from '@pkg/sanity-utils/types/button';
import { ButtonGroup } from '@pkg/modular-blocks/blocks/inner/ButtonGroup';
import ModularBlocksRenderer from '@pkg/modular-blocks/frontend/ModularBlocksRenderer.vue';
import { useResolvedSectionTheme } from '@pkg/modular-blocks/frontend/useResolvedSectionTheme';

const props = defineProps<{
  theme: THEME;
  slides?: Array<{
    _key: string;
    content?: Array<ModularBlock>;
    image?: Image | null;
    subImage?: Image | null;
  }>;
  reverse?: boolean;
  buttons?: Array<Button>;
}>();

const resolvedTheme = useResolvedSectionTheme(() => props.theme);

/** Host app registers these (PartialSection, BaseImage). */
const PartialSection = resolveComponent('PartialSection');
const BaseImage = resolveComponent('BaseImage');

const api = ref<CarouselApi>();
const currentStep = ref(0);
const showStepCounter = props.slides?.length && props.slides?.length > 1;

const setApi = (val: CarouselApi) => {
  api.value = val;
};

watchOnce(api, (api) => {
  if (!api) {
    return;
  }
  currentStep.value = api.selectedScrollSnap();

  api.on('select', () => {
    currentStep.value = api.selectedScrollSnap();
  });
});
</script>

<template>
  <component :is="PartialSection" :theme="resolvedTheme || THEME.DARK">
    <div
      :class="
        cn('c-steps-section wrapper', {
          'c-steps-section--one-image': slides?.[currentStep]?.subImage?.asset == null,
          'c-steps-section__reverse': props.reverse,
        })
      "
    >
      <slot>
        <Carousel class="flex-1 w-full flex flex-row justify-center" @init-api="setApi">
          <div class="c-steps-section__wrapper flex-1">
            <CarouselContent class="w-full">
              <CarouselItem v-for="slide in slides" :key="slide?._key">
                <div
                  :class="
                    cn('c-steps-section__content content-container', {
                      'c-steps-section__content--one-image':
                        slide?.subImage == null,
                    })
                  "
                >
                  <ModularBlocksRenderer :blocks="slide?.content" />
                </div>
              </CarouselItem>
            </CarouselContent>
            <CarouselStepCounter v-if="showStepCounter" :initial-count="slides?.length" />
            <ButtonGroup v-if="buttons" :buttons="buttons" />
          </div>
        </Carousel>
      </slot>
      <div class="c-steps-section__images">
        <Transition name="image-fade" mode="default">
          <div
            :key="currentStep"
            class="inset-0 absolute flex flex-row justify-center desk:justify-end items-center"
          >
            <component
              :is="BaseImage"
              v-if="slides?.[currentStep]?.image != null"
              :key="`main-image-${currentStep}`"
              :image="slides?.[currentStep]?.image"
              class="c-steps-section__main-image"
              :width="627"
            />
            <component
              :is="BaseImage"
              v-if="slides?.[currentStep]?.subImage != null"
              :key="`sub-image-${currentStep}`"
              :image="slides?.[currentStep]?.subImage"
              class="c-steps-section__sub-image animate-in fade-in duration-tortoise ease-out"
              :width="286"
            />
          </div>
        </Transition>
      </div>
    </div>
  </component>
</template>

<style scoped>
.c-steps-section {
  --sub-image-offset: 45px;

  @screen desk {
    --sub-image-offset: 102px;
  }

  display: flex;
  flex-direction: column-reverse;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;

  min-height: 100%;

  @screen desk {
    gap: 1.5rem;
    flex-direction: row;
    margin-bottom: 0;
  }

  &__reverse {
    @screen desk {
      flex-direction: row-reverse;
    }
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    gap: 1.5rem;

    max-width: 486px;
    min-height: 0;

    @screen desk {
      gap: 2.5rem;
      align-items: flex-start;
      margin-left: 102px;
      text-align: unset;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 1rem;

    max-width: 486px;

    @screen desk {
      gap: 1.5rem;
      max-width: 384px;

      &--one-image {
        max-width: 486px;
      }
    }
  }

  --main-image-size: 275px;
  --sub-image-size: 125px;

  @screen desk {
    --main-image-size: 627px;
    --sub-image-size: 286px;
  }

  &__images {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: var(--main-image-size);
    min-height: var(--main-image-size);
    margin-left: var(--sub-image-offset);
    margin-bottom: var(--sub-image-offset);

    @screen desk {
      margin-right: calc(-1 * var(--wrapper-padding));
    }
  }

  &--one-image {
    & .c-steps-section__images {
      margin-top: calc(0.5 * var(--sub-image-offset));
      margin-bottom: calc(0.5 * var(--sub-image-offset));
    }

    & .c-steps-section__main-image {
      --main-image-size: 320px;
      position: relative;
      transform: translateX(calc(-0.5 * var(--sub-image-offset)));

      @screen desk {
        margin-right: var(--wrapper-padding);
        --main-image-size: auto;
        transform: none;
      }
    }
  }

  &__main-image {
    object-fit: cover;
    position: absolute;
    top: 0;
    right: 0;

    width: var(--main-image-size);
    max-width: var(--main-image-size);
    height: var(--main-image-size);
    max-height: var(--main-image-size);
  }

  &__sub-image {
    position: absolute;
    bottom: calc(-1 * var(--sub-image-offset));
    left: calc(-1 * var(--sub-image-offset));
    width: var(--sub-image-size);
    height: var(--sub-image-size);
    object-fit: cover;
  }
}

/*
  Enter and leave animations can use different
  durations and timing functions.
*/
.image-fade-enter-active {
  transition: all theme("animationDuration.tortoise") ease-out;
}

.image-fade-leave-active {
  transition: all theme("animationDuration.tortoise") ease-out;
  z-index: 2;
}

.image-fade-enter {
  z-index: 1;
}

.image-fade-leave {
  opacity: 1;
  z-index: 2;
}

.image-fade-leave-to {
  opacity: 0;
  z-index: 2;
}
</style>
